import compareComps from "./compSort";
import { ROUND_DANCE_LABELS, roundDanceRoundOf } from "./cascade";

// The school's FULL entry (card 024af124): every dancer's competitions with
// the choices made for each (set dance, speed, round dance), every team's
// dance and figure, and the add-ons bought (céilí tickets, ads …). Teachers
// asked for one place to review and share this; the statement is money-only
// and the registration steps show one dancer or team at a time.
//
// Pure: the page passes in what it already loaded (roster, cart, teams,
// competitions, GET /reference, GET /add-ons).

export const speedLabel = (v) => {
  if (v == null || v === "") return "";
  if (v === "traditional") return "Traditional";
  return /^\d+$/.test(String(v)) ? `${v} bpm` : String(v);
};

// One selection -> display strings. Keys resolve through /reference; an
// unknown key (catalog edited since) shows as stored rather than vanishing.
// comp (optional) gives the round a round dance belongs to; without it the
// round falls back to 2, the only round before roundDanceRound existed.
export const choiceFor = (sel, reference, comp = null) => {
  if (!sel) return { set: "", speed: "", roundDance: "", roundDanceRound: null };
  const sets = (reference && reference.setDances) || [];
  const rounds = (reference && reference.roundDances) || [];
  const set = sel.danceChoice
    ? sets.find((s) => s.key === sel.danceChoice || s.name === sel.danceChoice)
    : null;
  const round = sel.roundDance ? rounds.find((r) => r.key === sel.roundDance) : null;
  return {
    set: sel.danceChoice ? (set && set.name) || sel.danceChoice : "",
    speed: sel.danceChoice ? speedLabel(sel.speed) : "",
    roundDance: sel.roundDance
      ? (round && round.name) || ROUND_DANCE_LABELS[sel.roundDance] || sel.roundDance
      : "",
    roundDanceRound: sel.roundDance ? roundDanceRoundOf(comp) || 2 : null,
  };
};

const byComp = (a, b) => {
  if (a.comp && b.comp) return compareComps(a.comp, b.comp);
  if (a.comp) return -1;
  if (b.comp) return 1;
  return a.name.localeCompare(b.name);
};

// ageOf(birthday) -> the dancer's age for the event, or null.
export const buildFullEntry = ({ roster, cart, teams, competitions, reference, ageOf = () => null }) => {
  const compById = Object.fromEntries((competitions || []).map((c) => [String(c._id), c]));
  const rosterById = Object.fromEntries((roster || []).map((d) => [String(d._id), d]));
  const dancers = new Map();

  const addEntries = (did, compIds, selections, paid) => {
    const r = rosterById[did];
    if (!dancers.has(did)) {
      dancers.set(did, {
        id: did,
        name: r ? `${r.fname || ""} ${r.lname || ""}`.trim() || "Dancer" : "Dancer",
        age: r ? ageOf(r.birthday) : null,
        entries: [],
      });
    }
    const d = dancers.get(did);
    (compIds || []).forEach((cid) => {
      const key = String(cid);
      // A paid entry wins over the same comp sitting in the draft.
      if (d.entries.some((e) => e.compId === key)) return;
      const comp = compById[key] || null;
      const sel = (selections || []).find((s) => String(s.competition_id) === key);
      d.entries.push({
        compId: key,
        comp,
        name: (comp && (comp.name || comp.code)) || "Competition",
        code: (comp && comp.code) || "",
        paid,
        ...choiceFor(sel, reference, comp),
      });
    });
  };

  (roster || []).forEach((d) => {
    if (d.entry) addEntries(String(d._id), d.entry.competitions, d.entry.selections, true);
  });
  ((cart && cart.items) || []).forEach((it) =>
    addEntries(String(it.dancer_id), it.competitions, it.selections, false)
  );

  const soloDancers = [...dancers.values()]
    .filter((d) => d.entries.length)
    .map((d) => ({ ...d, entries: d.entries.sort(byComp) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const teamRows = (teams || [])
    .map((t) => {
      const populated = t.competition || {};
      const comp = compById[String(populated._id)] || (populated._id ? populated : null);
      return {
        id: String(t._id),
        comp,
        name: populated.name || populated.code || "Team",
        // "Team A": the school's teams in a competition, in registration order (card ac23c7e0)
        team: t.teamLetter ? `Team ${t.teamLetter}` : "",
        code: populated.code || "",
        dance: t.danceName || t.danceKey || "",
        figure: t.figure || "",
        members: (t.members || []).map((m) => ({
          name: [m.fname, m.lname].filter(Boolean).join(" ") || "Dancer",
          age: ageOf(m.birthday),
        })),
      };
    })
    .sort(byComp);

  return { dancers: soloDancers, teams: teamRows };
};

// "Round 3: Slow Treble Jig" — the round dance with the round it is danced in.
export const roundDanceText = (e) =>
  e && e.roundDance ? `Round ${e.roundDanceRound || 2}: ${e.roundDance}` : "";

// Spreadsheet of the full entry: one row per dancer per competition (team
// rows once per MEMBER, so it answers "what is this dancer in?"), then the
// add-on counts underneath.
// opts.addOnsFailed: the add-ons read failed, so say so rather than export a
// sheet that looks complete without them.
export const fullEntryCsvRows = (full, addOns, opts = {}) => {
  const age = (a) => (a != null ? "U" + a : "");
  const rows = [
    ["Dancer", "Age", "Competition", "Code", "Type", "Set dance", "Speed", "Round dance", "Team dance", "Figure", "Status"],
  ];
  full.dancers.forEach((d) =>
    d.entries.forEach((e) =>
      rows.push([d.name, age(d.age), e.name, e.code, "Solo", e.set, e.speed, roundDanceText(e), "", "", e.paid ? "Paid" : "Draft (unpaid)"])
    )
  );
  full.teams.forEach((t) =>
    t.members.forEach((m) =>
      rows.push([m.name, age(m.age), t.name, t.code, t.team || "Team", "", "", "", t.dance, t.figure, "Paid"])
    )
  );
  if (opts.addOnsFailed) {
    rows.push([]);
    rows.push(["Add-ons could not be loaded; download again to include them"]);
  } else if (addOns && addOns.length) {
    rows.push([]);
    rows.push(["Add-on", "Quantity"]);
    addOns.forEach((a) => rows.push([a.label, a.qty]));
  }
  return rows;
};
